/**
 * Screen 6 — the connector console.
 *
 * ONE component driven by a driver id, hitting the shared `/api/connectors/{name}`
 * surface. Forking it per module would produce two consoles that diverge within a
 * sprint, which is why the NFA driver and the VIM driver render through the same
 * code with different capabilities declared by the driver itself.
 *
 * Admin only. A vendor must never see another vendor's traffic, a mapping table,
 * a dead letter or a sync control.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Activity, AlertTriangle, CheckCircle2, Loader2, Play, Plug, RefreshCw,
  RotateCcw, Save, Timer,
} from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/api';
import { Btn, Empty, Field, Input, Panel, Stat } from '../nfa/NfaPrimitives';
import { int, num, stamp, useIptTheme } from '../ipt/iptTheme';
import { SapVimConnect } from './SapVimConnect';

/* The connector payloads are rendered verbatim, so the vendor host and acronym
   are masked at the display layer — a backend copy change cannot re-leak it. */
export const maskVendor = (s) => String(s)
  .replace(/https?:\/\/[^\s"']*autodesk[^\s"']*/gi, '3D model service endpoint')
  .replace(/\bAPS\b(?!_)/g, '3D model service')
  .replace(/\bAutodesk\b/g, '3D model service');

const Json = ({ value, T, testId }) => (
  <pre className="text-[10px] leading-relaxed rounded-md p-2.5 overflow-x-auto max-h-64"
    data-testid={testId}
    style={{ background: T.surfaceAlt, border: `1px solid ${T.border}`, color: T.body }}>
    {maskVendor(JSON.stringify(value ?? null, null, 2))}
  </pre>
);

export const ConnectorConsole = ({ name, title }) => {
  const T = useIptTheme();
  const [d, setD] = useState({});
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);
  const [onlyErrors, setOnlyErrors] = useState(false);
  const [cfgDraft, setCfgDraft] = useState(null);
  const [advance, setAdvance] = useState(5);

  const base = `/connectors/${name}`;

  const load = useCallback(async () => {
    const get = (ep) => api.get(`${base}/${ep}`).then((r) => r.data).catch(() => null);
    const [health, config, mappings, log, dlq, throughput, recon, sim] = await Promise.all([
      get('health'), get('config'), get('mappings'),
      get(`log?limit=60${onlyErrors ? '&only_errors=true' : ''}`),
      get('dlq'), get('throughput'), get('reconciliation'), get('simulator'),
    ]);
    setD({ health, config, mappings, log, dlq, throughput, recon, sim });
    setLoading(false);
  }, [base, onlyErrors]);

  useEffect(() => {
    load();
    const t = setInterval(load, 20000);
    return () => clearInterval(t);
  }, [load]);

  const run = async (kind, params, msg) => {
    setBusy(kind);
    try {
      const r = await api.post(`${base}/sync/${kind}`, params || {});
      toast.success(msg || `${kind} complete`, {
        description: typeof r.data === 'object'
          ? Object.entries(r.data).slice(0, 4).map(([k, v]) => `${k}: ${v}`).join(' · ')
          : undefined,
      });
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || `${kind} failed`);
    } finally { setBusy(null); }
  };

  const replay = async (id) => {
    setBusy(`dlq-${id}`);
    try {
      const r = await api.post(`${base}/dlq/${id}/replay`);
      if (r.data?.ok) toast.success('Re-driven through the connector');
      else toast.error(r.data?.error || 'Replay failed again — still in the queue');
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Replay failed');
    } finally { setBusy(null); }
  };

  const testConn = async () => {
    setBusy('test');
    try {
      const r = await api.post(`${base}/test`);
      (r.data?.ok ? toast.success : toast.error)(
        r.data?.ok ? 'Handshake succeeded' : (r.data?.error || 'Handshake failed'),
        { description: r.data?.detail || r.data?.transport });
    } catch { toast.error('Handshake failed'); } finally { setBusy(null); }
  };

  const saveCfg = async () => {
    setBusy('config');
    try {
      await api.put(`${base}/config`, cfgDraft);
      toast.success('Connector settings saved');
      setCfgDraft(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not save');
    } finally { setBusy(null); }
  };

  const h = d.health;
  const caps = useMemo(() => new Set(h?.capabilities || []), [h]);

  if (loading) {
    return (
      <div className="py-20 text-center" data-testid="connector-loading">
        <Loader2 className="w-5 h-5 animate-spin mx-auto" style={{ color: T.primary }} />
      </div>
    );
  }
  if (!h) {
    return <Empty T={T} testId="connector-error">
      This connector did not answer. It may not be registered on this deployment.
    </Empty>;
  }

  const sim = d.sim || h.simulator || {};
  const tp = d.throughput || {};
  const dlqRows = d.dlq?.rows || [];
  const logRows = d.log?.rows || [];
  const mapRows = d.mappings?.rows || [];
  const latest = d.recon?.latest;

  return (
    <div className="space-y-4" data-testid="connector-console">
      {(h.mode === 'SIMULATED' || h.connected === false) && (
        <div className="px-3 py-2 rounded-md text-[11.5px] font-semibold flex items-start gap-2"
          data-testid="connector-sim-banner"
          style={{ background: T.tint(h.mode === 'LIVE' ? T.red : T.accent, '1F'),
                   color: h.mode === 'LIVE' ? T.red : T.accent,
                   border: `1px solid ${T.tint(h.mode === 'LIVE' ? T.red : T.accent, '44')}` }}>
          <Plug className="w-3.5 h-3.5 mt-px shrink-0" />
          <span>
            {h.labelled || 'SIMULATED — no live tenant connected'}
            {h.probe?.detail && h.probe.detail !== h.labelled && (
              <span className="block font-normal mt-0.5">{h.probe.detail}</span>
            )}
          </span>
        </div>
      )}

      {name === 'sap_vim' && <SapVimConnect name={name} health={h} onChanged={load} />}

      <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-3">
        <Stat T={T} testId="connector-stat-health" label="Handshake"
          value={h.ok ? 'reachable' : 'failing'}
          sub={`${h.transport || '—'} · ${h.mode}`} />
        <Stat T={T} testId="connector-stat-rate" label="Success rate (24 h)"
          value={tp.successRate === undefined ? '—' : `${num(tp.successRate, 1)}%`}
          sub={`${int(tp.ok)} ok · ${int(tp.failed)} failed of ${int(tp.calls)}`} />
        <Stat T={T} testId="connector-stat-latency" label="Latency p50 / p95"
          value={`${int(tp.p50Ms)} / ${int(tp.p95Ms)} ms`}
          sub="measured on the driver's own calls" />
        <Stat T={T} testId="connector-stat-dlq" label="Dead letters"
          value={int(tp.dlqDepth ?? dlqRows.length)}
          sub={dlqRows.length ? 'replayable below' : 'queue empty'} />
        <Stat T={T} testId="connector-stat-watermark" label="Delta watermark"
          value={h.lastDeltaApplied === undefined ? '—' : `${int(h.lastDeltaApplied)} applied`}
          sub={stamp(h.watermark)} />
        <Stat T={T} testId="connector-stat-backfill" label="Backfill"
          value={h.backfill?.status || '—'}
          sub={h.backfill ? `${int(h.backfill.processed)} of ${int(h.backfill.total)}` : '—'} />
      </div>

      <Panel T={T} testId="connector-actions" title="Sync and recovery"
        subtitle="Every action is logged with a correlation id and appears in the log below">
        <div className="flex flex-wrap items-end gap-2">
          <Btn T={T} onClick={testConn} disabled={busy === 'test'}
            testId="connector-test">
            <Activity className="w-3 h-3 mr-1" /> Test handshake
          </Btn>
          <Btn T={T} onClick={() => run('delta', {}, 'Delta poll complete')}
            disabled={busy === 'delta'} testId="connector-sync-delta">
            <RefreshCw className="w-3 h-3 mr-1" /> Delta poll now
          </Btn>
          <Btn T={T} variant="ghost"
            onClick={() => run('backfill', { resume: true }, 'Backfill resumed')}
            disabled={busy === 'backfill'} testId="connector-sync-backfill">
            Resume backfill
          </Btn>
          <Btn T={T} variant="ghost"
            onClick={() => run('reconcile', { window_days: 400 }, 'Reconciliation complete')}
            disabled={busy === 'reconcile'} testId="connector-sync-reconcile">
            Reconcile against the source
          </Btn>
          {sim.enabled !== undefined && (
            <>
              <Field T={T} label="advance n invoices">
                <Input T={T} type="number" value={advance} style={{ width: 84 }}
                  data-testid="connector-advance-count"
                  onChange={(e) => setAdvance(e.target.value)} />
              </Field>
              <Btn T={T} onClick={() => run('simulator-advance', { count: Number(advance) },
                'Simulator advanced')}
                disabled={busy === 'simulator-advance'} testId="connector-advance-now">
                <Play className="w-3 h-3 mr-1" /> Advance now
              </Btn>
              <Btn T={T} variant="ghost"
                onClick={() => run('simulator-toggle', { enabled: !sim.enabled },
                  sim.enabled ? 'Simulator paused' : 'Simulator resumed')}
                disabled={busy === 'simulator-toggle'} testId="connector-sim-toggle">
                {sim.enabled ? 'Pause simulator' : 'Resume simulator'}
              </Btn>
            </>
          )}
        </div>
        {sim.enabled !== undefined && (
          <div className="mt-2 flex items-center gap-3 text-[10.5px]" style={{ color: T.muted }}>
            <Timer className="w-3 h-3" />
            <span data-testid="connector-sim-state">
              simulator {sim.enabled ? 'running' : 'paused'} · every
              {' '}{int((sim.tickSeconds || 0) / 60)} min · last {stamp(sim.lastTick)} ·
              next {stamp(sim.nextTick)} · {int(sim.advancedTotal ?? sim.advanced)} stage moves
              generated{sim.seed ? ` · seed ${sim.seed}` : ''}
            </span>
          </div>
        )}
      </Panel>

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
        <Panel T={T} testId="connector-dlq" title="Dead letter queue"
          subtitle={dlqRows.length
            ? `${int(dlqRows.length)} record(s) the connector could not apply`
            : 'Empty — nothing has failed past its retries'}>
          <ol className="space-y-0">
            {dlqRows.map((r, i) => (
              <li key={r.dlq_id || i} className="py-2 text-[11px]"
                data-testid={`connector-dlq-${i}`}
                style={{ borderTop: i ? `1px solid ${T.border}` : 'none' }}>
                <div className="flex items-baseline gap-2">
                  <AlertTriangle className="w-3 h-3 shrink-0" style={{ color: T.red }} />
                  <span className="font-semibold" style={{ color: T.ink }}>
                    {r.kind || r.operation || 'record'}
                  </span>
                  <span style={{ color: T.body }}>{r.error || r.reason}</span>
                  <span className="text-[9.5px]" style={{ color: T.muted }}>
                    {int(r.attempts)} attempt(s) · {stamp(r.created_at || r.timestamp)}
                    {r.correlation_id ? ` · corr ${r.correlation_id}` : ''}
                  </span>
                  <span className="ml-auto">
                    <Btn T={T} variant="ghost" onClick={() => replay(r.dlq_id)}
                      disabled={busy === `dlq-${r.dlq_id}`}
                      testId={`connector-dlq-replay-${i}`}>
                      <RotateCcw className="w-3 h-3 mr-1" /> Replay
                    </Btn>
                  </span>
                </div>
              </li>
            ))}
            {!dlqRows.length && (
              <Empty T={T}>
                <CheckCircle2 className="w-4 h-4 mx-auto mb-1" style={{ color: T.green }} />
                No dead letters. A replay re-drives the record through the connector — it
                does not mark it resolved.
              </Empty>
            )}
          </ol>
        </Panel>

        <Panel T={T} testId="connector-recon" title="Reconciliation against the source"
          subtitle={latest
            ? `${int(latest.issues)} issue(s) · ${int(latest.vim_documents)} source documents `
              + `vs ${int(latest.panorix_invoices)} here · ${stamp(latest.started_at)}`
            : 'No run recorded yet'}>
          {latest ? (
            <>
              <div className="flex flex-wrap gap-2 mb-2">
                {Object.entries(latest.summary || {}).map(([k, v]) => (
                  <span key={k} data-testid={`connector-recon-${k}`}
                    className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[10px] font-semibold"
                    style={{ background: T.tint(v ? T.red : T.green, '1A'),
                             color: v ? T.red : T.green }}>
                    {k.replace(/_/g, ' ').toLowerCase()} {int(v)}
                  </span>
                ))}
              </div>
              <p className="text-[10px] mb-2" style={{ color: T.muted }}>
                Compared on {(latest.fieldsCompared || []).join(', ') || '—'} with a
                tolerance of {num(latest.amountTolerance, 2)}. A difference inside the
                tolerance is not reported, which is why the tolerance is stated.
              </p>
              <table className="w-full text-[10.5px]">
                <thead>
                  <tr style={{ color: T.muted }}>
                    {['Issue', 'Severity', 'Document', 'Vendor', 'Detail'].map((x) => (
                      <th key={x} className="py-1 px-1.5 text-left font-semibold">{x}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {(latest.rows || []).slice(0, 12).map((r, i) => (
                    <tr key={i} data-testid={`connector-recon-row-${i}`}
                      style={{ borderTop: `1px solid ${T.border}` }}>
                      <td className="py-1 px-1.5" style={{ color: T.ink }}>
                        {r.issue?.replace(/_/g, ' ').toLowerCase()}
                      </td>
                      <td className="py-1 px-1.5"
                        style={{ color: r.severity === 'HIGH' ? T.red : T.amber }}>
                        {r.severity}
                      </td>
                      <td className="py-1 px-1.5" style={{ color: T.body }}>{r.dp}</td>
                      <td className="py-1 px-1.5" style={{ color: T.body }}>
                        {r.vendor_invoice_no || r.vendor_id}
                      </td>
                      <td className="py-1 px-1.5" style={{ color: T.muted }}>{r.detail}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </>
          ) : <Empty T={T}>Run a reconciliation to compare both sides.</Empty>}
        </Panel>
      </div>

      <Panel T={T} testId="connector-log" title="Call log"
        subtitle={`${int(logRows.length)} most recent calls · correlation ids match the audit trail`}
        right={(
          <button type="button" onClick={() => setOnlyErrors((v) => !v)}
            data-testid="connector-log-errors-only"
            className="px-2 py-0.5 rounded text-[10px] font-semibold"
            style={onlyErrors
              ? { background: T.tint(T.red, '1F'), color: T.red }
              : { color: T.muted, border: `1px solid ${T.border}` }}>
            failures only
          </button>
        )}>
        <div className="overflow-x-auto">
          <table className="w-full text-[10.5px]" data-testid="connector-log-table">
            <thead>
              <tr style={{ color: T.muted, background: T.surfaceAlt }}>
                {['When', 'Direction', 'Call', 'Result', 'Applied', 'Correlation id',
                  'Reason'].map((x) => (
                    <th key={x} className="py-1.5 px-2 text-left font-semibold whitespace-nowrap">
                      {x}
                    </th>
                  ))}
              </tr>
            </thead>
            <tbody>
              {logRows.map((r, i) => (
                <tr key={r.log_id || i} data-testid={`connector-log-${i}`}
                  style={{ borderTop: `1px solid ${T.border}`,
                           background: r.ok ? 'transparent' : T.tint(T.red, '0E') }}>
                  <td className="py-1 px-2 tabular-nums whitespace-nowrap"
                    style={{ color: T.muted }}>{stamp(r.timestamp)}</td>
                  <td className="py-1 px-2" style={{ color: T.body }}>{r.direction}</td>
                  <td className="py-1 px-2" style={{ color: T.ink }}>{r.method}</td>
                  <td className="py-1 px-2 font-semibold"
                    style={{ color: r.ok ? T.green : T.red }}>
                    {r.ok ? 'ok' : 'failed'}
                  </td>
                  <td className="py-1 px-2 tabular-nums" style={{ color: T.body }}>
                    {r.advanced ?? '—'}
                  </td>
                  <td className="py-1 px-2 font-mono text-[9.5px]" style={{ color: T.muted }}>
                    {r.correlation_id}
                  </td>
                  <td className="py-1 px-2" style={{ color: T.muted }}>{r.reason}</td>
                </tr>
              ))}
              {!logRows.length && (
                <tr><td colSpan={7}><Empty T={T}>No calls logged.</Empty></td></tr>
              )}
            </tbody>
          </table>
        </div>
      </Panel>

      <Panel T={T} testId="connector-mappings" title="Field mappings"
        subtitle={`${int(mapRows.length)} mapped fields — what the source calls it, and what we do`}>
        <div className="overflow-x-auto">
          <table className="w-full text-[10.5px]" data-testid="connector-mappings-table">
            <thead>
              <tr style={{ color: T.muted, background: T.surfaceAlt }}>
                {['Source field', 'Target field', 'Transform', 'Direction', 'Note',
                  'Updated'].map((x) => (
                    <th key={x} className="py-1.5 px-2 text-left font-semibold">{x}</th>
                  ))}
              </tr>
            </thead>
            <tbody>
              {mapRows.map((r, i) => (
                <tr key={`${r.source_field}-${i}`} data-testid={`connector-map-${i}`}
                  style={{ borderTop: `1px solid ${T.border}` }}>
                  <td className="py-1 px-2 font-mono" style={{ color: T.ink }}>
                    {r.source_field}
                  </td>
                  <td className="py-1 px-2 font-mono" style={{ color: T.primary }}>
                    {r.target_field}
                  </td>
                  <td className="py-1 px-2" style={{ color: T.body }}>{r.transform || '—'}</td>
                  <td className="py-1 px-2" style={{ color: T.body }}>{r.direction}</td>
                  <td className="py-1 px-2" style={{ color: T.muted }}>{r.note}</td>
                  <td className="py-1 px-2 whitespace-nowrap" style={{ color: T.muted }}>
                    {stamp(r.updated_at)}{r.updated_by ? ` · ${r.updated_by}` : ''}
                  </td>
                </tr>
              ))}
              {!mapRows.length && (
                <tr><td colSpan={6}><Empty T={T}>No mappings declared.</Empty></td></tr>
              )}
            </tbody>
          </table>
        </div>
      </Panel>

      <Panel T={T} testId="connector-config" title="Configuration"
        subtitle="Secrets are masked by the server and never returned in full"
        right={cfgDraft ? (
          <div className="flex gap-2">
            <Btn T={T} variant="ghost" onClick={() => setCfgDraft(null)}
              testId="connector-config-cancel">Discard</Btn>
            <Btn T={T} onClick={saveCfg} disabled={busy === 'config'}
              testId="connector-config-save">
              <Save className="w-3 h-3 mr-1" /> Save
            </Btn>
          </div>
        ) : (
          <Btn T={T} variant="ghost" onClick={() => setCfgDraft(d.config || {})}
            testId="connector-config-edit">Edit</Btn>
        )}>
        {cfgDraft ? (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            {Object.entries(cfgDraft)
              .filter(([, v]) => typeof v !== 'object' || v === null)
              .map(([kk, vv]) => (
                <Field T={T} key={kk} label={kk.replace(/_/g, ' ')}>
                  <Input T={T} value={vv === null ? '' : String(vv)}
                    data-testid={`connector-cfg-${kk}`}
                    onChange={(e) => setCfgDraft({ ...cfgDraft, [kk]: e.target.value })} />
                </Field>
              ))}
          </div>
        ) : <Json value={d.config} T={T} testId="connector-config-json" />}
      </Panel>

      <p className="text-[10px]" style={{ color: T.muted }} data-testid="connector-footnote">
        {title || h.labelled} · driver <span className="font-mono">{name}</span> ·
        capabilities {(h.capabilities || Array.from(caps)).join(', ') || 'declared by the driver'}.
        This console is one component driven by the driver id — the same code renders every
        registered connector, so a fix here fixes all of them.
      </p>
    </div>
  );
};

export default ConnectorConsole;
